import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'

import ErrorEmitter from '@utils/emitters/ErrorEmitter'

export type APIError = {
  code?: string
  message?: string
}

export type TimestampedError = APIError & {
  timestamp: Date
}

export type APIErrorContextType = {
  errors: TimestampedError[]
  addError: (err: APIError) => void
  addErrors: (errors: APIError[]) => void
  clearError: (index: number) => void
  clearErrors: () => void
}

export const APIErrorContext = createContext<APIErrorContextType>({
  errors: [],
  addError: () => null,
  addErrors: () => null,
  clearError: () => null,
  clearErrors: () => null,
})

const APIErrorProvider: React.FC = ({ children }) => {
  const [errors, setErrors] = useState<TimestampedError[]>([])

  const addError = useCallback((err: APIError) => {
    // Only show last 3 errors
    setErrors((prevErrors) => [...prevErrors.slice(-2), { ...err, timestamp: new Date() }])
  }, [])

  const addErrors = useCallback((apiErrors: APIError[]) => {
    setErrors((prevErrors) => {
      const newErrors = [
        ...prevErrors,
        ...apiErrors.map((err) => ({ ...err, timestamp: new Date() })),
      ]
      // Only show last 3 errors
      return [...newErrors.slice(-3)]
    })
  }, [])

  const clearError = useCallback((index: number) => {
    setErrors((prevErrors) => [...prevErrors.filter((_, idx) => idx !== index)])
  }, [])

  const clearErrors = useCallback(() => setErrors([]), [])

  // Backup option to use emitters for components that don't use hooks
  useEffect(() => {
    ErrorEmitter.on('addError', addError)
    ErrorEmitter.on('addErrors', addErrors)
    ErrorEmitter.on('clearErrors', clearErrors)

    return () => {
      ErrorEmitter.off('addError', addError)
      ErrorEmitter.off('addErrors', addErrors)
      ErrorEmitter.off('clearErrors', clearErrors)
    }
  }, [addError, addErrors, clearError, clearErrors])

  const contextValue: APIErrorContextType = {
    errors,
    addError,
    addErrors,
    clearError,
    clearErrors,
  }
  return <APIErrorContext.Provider value={contextValue}>{children}</APIErrorContext.Provider>
}

export default APIErrorProvider

export const useAPIError = (): APIErrorContextType => useContext(APIErrorContext)
